/**
 * Product Discount Display
 * Dynamically shows discounts on product pages based on active cart discounts
 */
(function() {
  'use strict';

  let activeDiscount = null;
  let checkInterval = null;
  const CHECK_INTERVAL_MS = 60000; // Check every 60 seconds (1 minute)

  /**
   * Format price based on currency settings
   */
  function formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.money_format || '{{amount}}');
    }
    // Fallback: simple formatting
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * Get current variant ID from product page
   */
  function getCurrentVariantId() {
    // Try to get from variant input
    const variantInput = document.querySelector('input[name="id"]');
    if (variantInput && variantInput.value) {
      return variantInput.value;
    }
    
    // Try to get from product form
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const formVariantInput = productForm.querySelector('input[name="id"]');
      if (formVariantInput && formVariantInput.value) {
        return formVariantInput.value;
      }
    }
    
    // Try to get from product-info element
    const productInfo = document.querySelector('product-info');
    if (productInfo && productInfo.dataset.productId) {
      // This is a fallback - we'd need the variant ID, not product ID
      // But we can try to get the first available variant
      return null;
    }
    
    return null;
  }

  /**
   * Add product to cart temporarily to check for discounts
   */
  async function addProductToCheckDiscount(variantId) {
    if (!variantId) return null;
    
    try {
      // Add product to cart
      const addResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });
      
      if (!addResponse.ok) return null;
      
      // Fetch cart to check for discounts
      const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      if (!cartResponse.ok) {
        // Try to remove the item we just added
        await removeVariantFromCart(variantId);
        return null;
      }
      
      const cart = await cartResponse.json();
      
      // Find the item we just added
      const addedItem = cart.items.find(item => item.variant_id.toString() === variantId.toString());
      
      let discount = null;
      
      // Check for cart-level discounts (apply to all products)
      if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) {
        const cartDiscount = cart.cart_level_discount_applications[0];
        let discountValue = 0;
        let discountType = 'percentage';
        
        if (cartDiscount.value_type === 'percentage') {
          discountValue = parseFloat(cartDiscount.value);
          discountType = 'percentage';
        } else if (cartDiscount.value_type === 'fixed_amount') {
          discountValue = cartDiscount.value;
          discountType = 'fixed';
        }
        
        discount = {
          title: cartDiscount.title,
          value: discountValue,
          type: discountType,
          value_type: cartDiscount.value_type,
          isCartLevel: true,
          appliesToVariantId: null // Cart-level applies to all
        };
      }
      // Check for line-level discounts (product-specific)
      else if (addedItem && addedItem.line_level_discount_allocations && addedItem.line_level_discount_allocations.length > 0) {
        const discountAllocation = addedItem.line_level_discount_allocations[0];
        const lineDiscount = discountAllocation.discount_application;
        
        const originalPrice = addedItem.original_line_price;
        const discountAmount = discountAllocation.amount;
        const discountPercentage = (discountAmount / originalPrice) * 100;
        
        discount = {
          title: lineDiscount.title,
          value: discountPercentage,
          type: 'percentage',
          value_type: 'percentage',
          isCartLevel: false,
          appliesToVariantId: variantId.toString() // Only applies to this specific variant
        };
      }
      
      // Remove the item we just added
      await removeVariantFromCart(variantId);
      
      return discount;
    } catch (error) {
      console.error('Error checking discount by adding product:', error);
      // Try to clean up by removing the variant
      try {
        await removeVariantFromCart(variantId);
      } catch (e) {
        // Ignore cleanup errors
      }
      return null;
    }
  }

  /**
   * Remove variant from cart
   */
  async function removeVariantFromCart(variantId) {
    try {
      // First get the cart to find the item key
      const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      if (!cartResponse.ok) return;
      
      const cart = await cartResponse.json();
      const item = cart.items.find(item => item.variant_id.toString() === variantId.toString());
      
      if (!item) return;
      
      // Remove the item by setting quantity to 0
      await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          updates: {
            [item.key]: 0
          }
        })
      });
    } catch (error) {
      console.error('Error removing variant from cart:', error);
    }
  }

  /**
   * Fetch cart and detect active discounts
   */
  async function fetchCartDiscounts() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      if (!response.ok) return null;
      
      const cart = await response.json();
      
      // Check for cart-level discounts (apply to all products)
      if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) {
        const discount = cart.cart_level_discount_applications[0]; // Get first discount
        
        // Calculate discount percentage or fixed amount
        let discountValue = 0;
        let discountType = 'percentage';
        
        if (discount.value_type === 'percentage') {
          discountValue = parseFloat(discount.value);
          discountType = 'percentage';
        } else if (discount.value_type === 'fixed_amount') {
          discountValue = discount.value;
          discountType = 'fixed';
        }
        
        activeDiscount = {
          title: discount.title,
          value: discountValue,
          type: discountType,
          value_type: discount.value_type,
          isCartLevel: true,
          appliesToVariantId: null // Cart-level applies to all
        };
        
        return activeDiscount;
      }
      
      // Check for line-level discounts (product-specific)
      if (cart.items && cart.items.length > 0) {
        // Check if current variant is in cart and has a discount
        const variantId = getCurrentVariantId();
        if (variantId) {
          const currentItem = cart.items.find(item => item.variant_id.toString() === variantId.toString());
          if (currentItem && currentItem.line_level_discount_allocations && currentItem.line_level_discount_allocations.length > 0) {
            const discountAllocation = currentItem.line_level_discount_allocations[0];
            const discount = discountAllocation.discount_application;
            
            // Calculate percentage from allocation
            const originalPrice = currentItem.original_line_price;
            const discountAmount = discountAllocation.amount;
            const discountPercentage = (discountAmount / originalPrice) * 100;
            
            activeDiscount = {
              title: discount.title,
              value: discountPercentage,
              type: 'percentage',
              value_type: 'percentage',
              isCartLevel: false,
              appliesToVariantId: variantId.toString()
            };
            
            return activeDiscount;
          }
        }
      }
      
      // If cart is empty, try adding the current product to check for discounts
      if (cart.items.length === 0) {
        const variantId = getCurrentVariantId();
        if (variantId) {
          const discount = await addProductToCheckDiscount(variantId);
          if (discount) {
            activeDiscount = discount;
            return discount;
          }
        }
      }
      
      activeDiscount = null;
      return null;
    } catch (error) {
      console.error('Error fetching cart discounts:', error);
      return null;
    }
  }

  /**
   * Calculate discounted price
   */
  function calculateDiscountedPrice(originalPrice, discount) {
    if (!discount) return originalPrice;
    
    if (discount.type === 'percentage') {
      return Math.round(originalPrice * (1 - discount.value / 100));
    } else if (discount.type === 'fixed') {
      return Math.max(0, originalPrice - discount.value);
    }
    
    return originalPrice;
  }

  /**
   * Remove discount display from price element
   */
  function removeDiscountDisplay(priceElement) {
    if (!priceElement) return;
    
    // Check if discount was applied
    if (priceElement.dataset.discountApplied !== 'true') return;
    
    // Remove sale class
    priceElement.classList.remove('price--on-sale');
    
    // Reset the discount applied flag
    priceElement.dataset.discountApplied = 'false';
    
    // The regular price div should be visible again (CSS handles this)
    // The sale price div will be hidden (CSS handles this)
    // We don't need to modify the HTML structure as CSS will handle visibility
  }

  /**
   * Update price display with discount
   */
  function updatePriceDisplay(priceElement, discount) {
    if (!priceElement) return;
    
    // If no discount, remove any existing discount display
    if (!discount) {
      removeDiscountDisplay(priceElement);
      return;
    }
    
    // Check if discount applies to this product
    // Only show if it's a cart-level discount (applies to all) OR if it applies to the current variant
    if (!discount.isCartLevel) {
      const currentVariantId = getCurrentVariantId();
      if (!currentVariantId || discount.appliesToVariantId !== currentVariantId.toString()) {
        // This discount doesn't apply to the current product
        return;
      }
    }
    
    const originalPrice = parseInt(priceElement.dataset.originalPrice);
    const compareAtPrice = parseInt(priceElement.dataset.compareAtPrice) || 0;
    
    if (!originalPrice) return;
    
    // Don't show discount if there's already a compare_at_price sale
    if (compareAtPrice > originalPrice) return;
    
    const discountedPrice = calculateDiscountedPrice(originalPrice, discount);
    
    // Only show discount if it's actually different
    if (discountedPrice >= originalPrice) return;
    
    const priceContainer = priceElement.querySelector('.price__container');
    if (!priceContainer) return;
    
    // Check if discount is already applied
    if (priceElement.dataset.discountApplied === 'true') return;
    
    // Get the regular and sale price divs
    const regularPriceDiv = priceContainer.querySelector('.price__regular');
    const salePriceDiv = priceContainer.querySelector('.price__sale');
    
    if (!regularPriceDiv || !salePriceDiv) return;
    
    // Add sale class to show the sale price div
    priceElement.classList.add('price--on-sale');
    
    // Update sale price div with discount
    const regularPriceItem = regularPriceDiv.querySelector('.price-item--regular');
    const salePriceItem = salePriceDiv.querySelector('.price-item--sale');
    
    if (regularPriceItem && salePriceItem) {
      // Get the original price text
      const originalPriceText = regularPriceItem.textContent.trim();
      
      // Get the original price element to preserve its structure
      const originalPriceSpan = regularPriceItem.cloneNode(true);
      originalPriceSpan.innerHTML = originalPriceText;
      
      // Create new sale price structure
      salePriceDiv.innerHTML = '';
      
      const regularPriceLabel = document.createElement('span');
      regularPriceLabel.className = 'visually-hidden visually-hidden--inline';
      regularPriceLabel.textContent = 'Regular price';
      
      const strikethroughWrapper = document.createElement('span');
      const strikethroughPrice = document.createElement('s');
      strikethroughPrice.className = 'price-item price-item--regular';
      strikethroughPrice.textContent = originalPriceText;
      strikethroughWrapper.appendChild(strikethroughPrice);
      
      const salePriceLabel = document.createElement('span');
      salePriceLabel.className = 'visually-hidden visually-hidden--inline';
      salePriceLabel.textContent = 'Sale price';
      
      const salePriceSpan = document.createElement('span');
      salePriceSpan.className = 'price-item price-item--sale price-item--last';
      salePriceSpan.textContent = formatMoney(discountedPrice);
      
      salePriceDiv.appendChild(regularPriceLabel);
      salePriceDiv.appendChild(strikethroughWrapper);
      salePriceDiv.appendChild(salePriceLabel);
      salePriceDiv.appendChild(salePriceSpan);
      
      // Hide regular price div (CSS will handle this with price--on-sale class)
      priceElement.dataset.discountApplied = 'true';
    }
  }

  /**
   * Update all price displays on the page
   */
  function updateAllPrices() {
    const priceElements = document.querySelectorAll('.price[data-original-price]');
    priceElements.forEach(priceElement => {
      // Always call updatePriceDisplay - it will remove discount if activeDiscount is null
      updatePriceDisplay(priceElement, activeDiscount);
    });
  }

  /**
   * Re-check discounts and update display
   */
  async function recheckDiscounts() {
    const previousDiscount = activeDiscount ? JSON.stringify(activeDiscount) : null;
    
    await fetchCartDiscounts();
    
    const currentDiscount = activeDiscount ? JSON.stringify(activeDiscount) : null;
    
    // Always update if discount changed (including when it's removed)
    if (previousDiscount !== currentDiscount) {
      // Reset discount applied flag so we can update/remove the display
      document.querySelectorAll('.price[data-original-price]').forEach(el => {
        el.dataset.discountApplied = 'false';
      });
      updateAllPrices();
    }
  }

  /**
   * Start periodic discount checking
   */
  function startPeriodicChecking() {
    // Clear any existing interval
    if (checkInterval) {
      clearInterval(checkInterval);
    }
    
    // Only check periodically if on a product page
    if (document.querySelector('product-info')) {
      // Check every minute
      checkInterval = setInterval(() => {
        recheckDiscounts();
      }, CHECK_INTERVAL_MS);
    }
  }

  /**
   * Stop periodic discount checking
   */
  function stopPeriodicChecking() {
    if (checkInterval) {
      clearInterval(checkInterval);
      checkInterval = null;
    }
  }

  /**
   * Initialize discount display
   */
  async function initDiscountDisplay() {
    // Only run on product pages
    if (!document.querySelector('product-info')) return;
    
    // Wait a bit for variant inputs to be ready
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // First, clear any existing discount display (in case discount was turned off)
    document.querySelectorAll('.price[data-original-price]').forEach(el => {
      el.dataset.discountApplied = 'false';
    });
    
    // Fetch discount information
    await fetchCartDiscounts();
    
    // Always update prices (will remove discount if activeDiscount is null)
    updateAllPrices();
    
    // Start periodic checking
    startPeriodicChecking();
    
    // Stop checking when page becomes hidden (user switches tabs)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        stopPeriodicChecking();
      } else {
        // Re-check immediately when page becomes visible again
        recheckDiscounts();
        startPeriodicChecking();
      }
    });
    
    
    // Listen for variant changes
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, async () => {
        // Reset discount applied flag
        document.querySelectorAll('.price[data-original-price]').forEach(el => {
          el.dataset.discountApplied = 'false';
        });
        
        // Re-check discounts for the new variant (in case cart is empty)
        await fetchCartDiscounts();
        setTimeout(updateAllPrices, 100);
      });
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDiscountDisplay);
  } else {
    initDiscountDisplay();
  }
  
  // Clean up interval when page unloads
  window.addEventListener('beforeunload', () => {
    stopPeriodicChecking();
  });
  
  // Also update when cart changes
  if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      fetchCartDiscounts().then(() => {
        document.querySelectorAll('.price[data-original-price]').forEach(el => {
          el.dataset.discountApplied = 'false';
        });
        updateAllPrices();
      });
    });
  }
})();

